import { useEffect, useRef } from 'react';

let SCRIPT_PROMISE = null;

/**
 * Loads the Instagram embed script once, and (re-)processes the embeds.
 * Instagram's official approach: drop in their <blockquote class="instagram-media">
 * markup and call window.instgrm.Embeds.process().
 */
function loadInstagramEmbed() {
  if (window.instgrm) {
    window.instgrm.Embeds.process();
    return Promise.resolve();
  }
  if (!SCRIPT_PROMISE) {
    SCRIPT_PROMISE = new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = 'https://www.instagram.com/embed.js';
      s.async = true;
      s.onload = () => {
        if (window.instgrm) window.instgrm.Embeds.process();
        resolve();
      };
      document.body.appendChild(s);
    });
  }
  return SCRIPT_PROMISE;
}

/**
 * Embeds a single public Instagram reel/post by URL.
 * Renders as a responsive vertical card. Aspect ratio is honoured by Instagram's
 * script. We constrain the wrapper max-width for a uniform grid look.
 */
export default function InstagramEmbed({ url, caption }) {
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadInstagramEmbed().then(() => {
      if (cancelled) return;
      // Re-run process when this embed mounts late
      if (window.instgrm) window.instgrm.Embeds.process();
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div className="flex flex-col" data-testid="instagram-embed">
      <div
        ref={ref}
        className="bg-[hsl(var(--bb-ivory-soft))] border border-[hsl(var(--bb-text))]/10 overflow-hidden"
      >
        <blockquote
          className="instagram-media"
          data-instgrm-permalink={url}
          data-instgrm-version="14"
          style={{
            background: 'transparent',
            border: 0,
            margin: 0,
            padding: 0,
            width: '100%',
            minWidth: '280px',
          }}
        >
          <a href={url} target="_blank" rel="noreferrer">
            View this post on Instagram
          </a>
        </blockquote>
      </div>
      {caption && (
        <p className="mt-3 text-sm text-[hsl(var(--bb-text-muted))]">{caption}</p>
      )}
    </div>
  );
}
